import type { Ambient, LightSource } from '../../shared/lighting.ts';

type Rgb = readonly [number, number, number];

/**
 * Teinte de l'obscurité : un bleu nuit très sombre, qui tire sur le prune au lever et au coucher du soleil. Elle
 * varie du haut de l'écran (l'horizon, encore un peu éclairé) au bas (déjà plongé dans la nuit).
 */
const SHADOW: Rgb = [8, 10, 28];
const SHADOW_HORIZON: Rgb = [22, 30, 66];
const DUSK_SHADOW: Rgb = [52, 12, 58];
const DUSK_SHADOW_HORIZON: Rgb = [92, 30, 62];
/** Écart d'obscurité entre le haut et le bas de l'écran, au cœur de la nuit (en part de l'obscurité). */
const SKY_SPREAD = 0.32;
/**
 * Teintes du ciel au fil de la transition, selon la part de nuit (0 : jour, 1 : nuit) : le soir, de l'or à l'orange,
 * au rouge puis au magenta et au violet ; le matin, plus doré et plus rose.
 */
const SUNSET: readonly (readonly [number, Rgb])[] = [[0, [255, 186, 34]], [0.3, [255, 106, 16]], [0.55, [244, 46, 52]], [0.8, [206, 32, 128]], [1, [104, 34, 176]]];
const SUNRISE: readonly (readonly [number, Rgb])[] = [[0, [255, 210, 90]], [0.3, [255, 140, 66]], [0.55, [255, 86, 132]], [0.8, [214, 74, 196]], [1, [118, 80, 214]]];
/**
 * Teinte au plus fort, en haut de l'écran (l'horizon, côté soleil) et en bas : un passage en « overlay » qui
 * colore sans effacer les détails, puis un voile léger qui la rend visible même sur le sombre.
 */
const DUSK_ALPHA = 0.38;
const DUSK_FAR_ALPHA = 0.24;
const DUSK_GLAZE_ALPHA = 0.07;
/** La teinte garde sa force plus longtemps autour du coucher (1 : montée linéaire, plus petit : plus tôt et plus tard). */
const DUSK_SPREAD = 0.7;
/** Le bas de l'écran a déjà la teinte d'un peu plus tard (plus proche de la nuit) : or en haut, violet en bas. */
const DUSK_FAR_SHIFT = 0.5;
/** Résolution des calques de lumière : un point pour LIGHT_PIXEL pixels d'écran, lissé en l'agrandissant. */
const LIGHT_PIXEL = 4;
/** Opacité de la lueur colorée des lumières, à intensité et obscurité maximales. */
const GLOW_ALPHA = 0.35;

/** Une lumière à l'écran : centre et portée en pixels. */
export interface LitPoint {
  x: number;
  y: number;
  radius: number;
  light: LightSource;
  /** Décale le vacillement d'une lumière à l'autre. */
  seed: number;
}

const mix = (a: Rgb, b: Rgb, t: number): Rgb => [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t, a[2] + (b[2] - a[2]) * t];
const css = ([r, g, b]: Rgb, alpha: number): string => `rgba(${Math.round(r)}, ${Math.round(g)}, ${Math.round(b)}, ${alpha})`;

/** Teinte d'un dégradé de couleurs à cette position (0 à 1). */
function sample(stops: readonly (readonly [number, Rgb])[], at: number): Rgb {
  const t = Math.min(1, Math.max(0, at));
  for (let i = 1; i < stops.length; i++) {
    const [end, to] = stops[i], [start, from] = stops[i - 1];
    if (t <= end) return mix(from, to, (t - start) / (end - start));
  }
  return stops[stops.length - 1][1];
}

const rgb = (hex: string): string => `${parseInt(hex.slice(1, 3), 16)}, ${parseInt(hex.slice(3, 5), 16)}, ${parseInt(hex.slice(5, 7), 16)}`;

/**
 * Obscurité d'une carte, percée par les lumières : un calque sombre dont chaque lumière retire un disque dégradé,
 * puis une lueur de sa couleur ajoutée par-dessus. Calculé en basse résolution (c'est flou de toute façon).
 */
export class LightLayer {
  private readonly shadow = document.createElement('canvas');
  private readonly glow = document.createElement('canvas');

  draw(target: CanvasRenderingContext2D, width: number, height: number, { darkness, dusk, night, rising }: Ambient, points: readonly LitPoint[], now: number): void {
    if (darkness <= 0.005) return;
    const w = Math.ceil(width / LIGHT_PIXEL), h = Math.ceil(height / LIGHT_PIXEL);
    const shadow = this.context(this.shadow, w, h), glow = this.context(this.glow, w, h);
    shadow.globalCompositeOperation = 'source-over';
    shadow.clearRect(0, 0, w, h);
    shadow.fillStyle = this.sky(shadow, h, darkness, dusk, night);
    shadow.fillRect(0, 0, w, h);
    shadow.globalCompositeOperation = 'destination-out';
    glow.globalCompositeOperation = 'source-over';
    glow.clearRect(0, 0, w, h);
    glow.globalCompositeOperation = 'lighter';
    for (const { x, y, radius, light, seed } of points) {
      // Vacillement : deux oscillations mêlées, d'autant plus rapides que `light.flicker` est grand.
      const t = now * light.flicker;
      const flicker = light.flicker ? 1 + 0.06 * Math.sin(t / 97 + seed) + 0.04 * Math.sin(t / 41 + seed * 1.7) : 1;
      const r = (radius * flicker) / LIGHT_PIXEL, cx = x / LIGHT_PIXEL, cy = y / LIGHT_PIXEL;
      if (r <= 0 || cx + r < 0 || cy + r < 0 || cx - r > w || cy - r > h) continue;
      const intensity = light.intensity * (light.flicker ? 0.93 + 0.07 * Math.sin(t / 63 + seed * 2.3) : 1);
      const hole = shadow.createRadialGradient(cx, cy, 0, cx, cy, r);
      hole.addColorStop(0, `rgba(0, 0, 0, ${intensity})`);
      hole.addColorStop(0.45, `rgba(0, 0, 0, ${intensity * 0.75})`);
      hole.addColorStop(1, 'rgba(0, 0, 0, 0)');
      shadow.fillStyle = hole;
      shadow.fillRect(cx - r, cy - r, r * 2, r * 2);
      const color = rgb(light.color);
      const halo = glow.createRadialGradient(cx, cy, 0, cx, cy, r);
      halo.addColorStop(0, `rgba(${color}, ${intensity * darkness * GLOW_ALPHA})`);
      halo.addColorStop(1, `rgba(${color}, 0)`);
      glow.fillStyle = halo;
      glow.fillRect(cx - r, cy - r, r * 2, r * 2);
    }
    target.save();
    target.imageSmoothingEnabled = true;
    target.drawImage(this.shadow, 0, 0, w * LIGHT_PIXEL, h * LIGHT_PIXEL);
    // La teinte par-dessus l'obscurité (qui la délaverait), sous la lueur des lumières (qui restent de leur couleur).
    if (dusk > 0) this.drawDusk(target, width, height, dusk ** DUSK_SPREAD, night, rising);
    target.globalCompositeOperation = 'lighter';
    target.drawImage(this.glow, 0, 0, w * LIGHT_PIXEL, h * LIGHT_PIXEL);
    target.restore();
  }

  /**
   * Obscurité de haut en bas de l'écran : plus claire et plus bleue vers l'horizon, plus dense en bas. Uniforme
   * pour une obscurité permanente (grotte), où il n'y a pas de ciel.
   */
  private sky(shadow: CanvasRenderingContext2D, h: number, darkness: number, dusk: number, night: number): CanvasGradient | string {
    const bottom = mix(SHADOW, DUSK_SHADOW, dusk);
    if (night <= 0) return css(bottom, darkness);
    const top = mix(SHADOW_HORIZON, DUSK_SHADOW_HORIZON, dusk);
    const spread = SKY_SPREAD * night;
    const gradient = shadow.createLinearGradient(0, 0, 0, h);
    gradient.addColorStop(0, css(top, darkness * (1 - spread)));
    gradient.addColorStop(0.45, css(mix(top, bottom, 0.6), darkness));
    gradient.addColorStop(1, css(bottom, Math.min(1, darkness * (1 + spread / 2))));
    return gradient;
  }

  /** Teinte du lever ou du coucher du soleil, de haut en bas : dorée et vive vers l'horizon, violette et plus sombre en bas. */
  private drawDusk(target: CanvasRenderingContext2D, width: number, height: number, dusk: number, night: number, rising: boolean): void {
    const stops = rising ? SUNRISE : SUNSET;
    const at = [0, 0.3, 0.65, 1].map((t) => [t, sample(stops, night + DUSK_FAR_SHIFT * t)] as const);
    const sky = (scale: number) => {
      const gradient = target.createLinearGradient(0, 0, 0, height);
      for (const [t, color] of at) gradient.addColorStop(t, css(color, (DUSK_ALPHA + (DUSK_FAR_ALPHA - DUSK_ALPHA) * t) * dusk * scale));
      return gradient;
    };
    target.globalCompositeOperation = 'overlay';
    target.fillStyle = sky(1);
    target.fillRect(0, 0, width, height);
    target.globalCompositeOperation = 'source-over';
    target.fillStyle = sky(DUSK_GLAZE_ALPHA / DUSK_ALPHA);
    target.fillRect(0, 0, width, height);
  }

  private context(canvas: HTMLCanvasElement, width: number, height: number): CanvasRenderingContext2D {
    if (canvas.width !== width || canvas.height !== height) Object.assign(canvas, { width, height });
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Canvas 2D indisponible.');
    return ctx;
  }
}
